import { LARegion } from "@dana/Common/Constants";
import { setOrderTypeCartModeCookie } from "@dana/Common/utils";
import ProductAvailabilityDetails from "@dana/Components/CheckAvailability/ProductAvailabilityDetails";
import getProductAvailability, { OrderType, OrderTypes } from "@dana/Handlers/ProductAvailabilityHandler";
import WarningIcon from "@dana/Icons/WarningIcon";
import { fontWeights } from "@dana/MobiusOverrides/styles/typography";
import { getProduct } from "@dana/Services/DanaProductAvailabilityService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import ProductAvailabilityState from "@dana/Store/Data/ProductAvailability/ProductAvailabilityState";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getLanguages, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { LanguageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getCookie, setCookie } from "@insite/mobius/utilities/cookies";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
}


const mapStateToProps = (state: ApplicationStateDana) => {
    const { isSearching, searchResults } = getProductSelector(state);
    const productAvailabilityState = state.productAvailabilityState;
    const session = getSession(state);
    const billtoCustomer = getBillToState(state, state.context.session.billToId);
    const shiptoCustomer = getShipToState(state, state.context.session.shipToId);
    const currentLanguage = state.context.session.language;
    const languages = getLanguages(state);
    return {
        languages,
        currentLanguage,
        isSearching,
        searchResults,
        productAvailabilityState,
        location: getLocation(state),
        session,
        billtoCustomer,
        shiptoCustomer

    };
};

const mapDispatchToProps = {
    searchProducts,
    clearProduct,
    setUnitOfMeasure,
    reset,
    getProductAvailability,
    updateProductInfo
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: GridContainerProps;
    searchGridItem?: GridItemProps;
    partNoSearhGrid?: GridItemProps;
    radioGroupContainer?: GridContainerProps;
    orderTypeSelection?: RadioGroupProps;
    orderTypeStyle?: RadioComponentProps;
    qtyGridItem?: GridItemProps;
    unitOfMeasureGridItem?: GridItemProps;
    errorMsgBox?: InjectableCss;
    unitOfMeasureSelect?: SelectPresentationProps;
    partNoSearch?: TextFieldPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    selectButton?: ButtonPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    selectedProductModal?: ModalPresentationProps;
    selecteProductModalTitle?: InjectableCss;
    selectedProductErpNo?: TypographyPresentationProps;
    selecteProductTitle?: TypographyPresentationProps;
    orderType?: TypographyPresentationProps;
}

export const productSelectorStyles: ProductSelectorStyles = {
    container: {
        gap: 0,
    },
    searchGridItem: {
        width: 12,
        css: css`
            position: relative;
            padding-top: 24px;
        `,
    },
    partNoSearhGrid: {
        css: css`
            column-gap: 8px;
        `,
    },
    radioGroupContainer: {
        gap: 0,
    },
    orderTypeSelection: {
        horizontal: true,
        css: css`
            margin-bottom: 24px;

            > ${TypographyStyle} {
                margin: 0;
            }
        `,
    },
    qtyGridItem: {
        width: [3, 3, 2, 1, 1],
    },
    unitOfMeasureGridItem: {
        width: [9, 9, 3, 3, 3],
    },
    errorMsgBox: {
        css: css`
            display: flex;
            align-items: center;
            gap: 4px;
            margin-top: 8px;
            width: 100%;
        `,
    },
    optionWrapper: {
        css: css``,
    },
    imageWrapper: {
        css: css``,
    },
    productImage: {
        width: "50px",
        css: css``,
    },
    infoWrapper: {
        css: css``,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css``,
    },
    selectButton: {
        css: css`
            margin-top: auto;
        `,
    },
    selectedProductModal: {
        size: 800,
    },
    selecteProductModalTitle: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 4px;
        `,
    },
    selectedProductErpNo: {
        variant: "h4",
        weight: fontWeights?.bold,
        css: css`
            margin-bottom: 0;
        `
    },
    selecteProductTitle: {
        variant: "h4",
        weight: fontWeights?.regular,
        css: css`
            margin-bottom: 0;
        `
    },
    orderType: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
            flex-shrink: 0;
            flex: 1;
        `,
    },
};

const ENTER_KEY = 13;

const CheckAvailabilityProductSelector = ({
    searchProducts,
    isSearching,
    searchResults,
    extendedStyles,
    customErrorMessage,
    location,
    reset,
    getProductAvailability,
    productAvailabilityState,
    session,
    billtoCustomer,
    shiptoCustomer,
    languages,
    currentLanguage,
}: Props) => {
    const orderTypeCookie = getCookie('modalOrderType');
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageModel>();
    const [isLoading, setIsLoading] = useState<boolean | undefined>(false);
    const [qty, setQty] = useState<number>(1);
    const [avilabilityModel, setAvilabilityModel] = useState(false);
    const [productAvilabilityModel, setProductAvilabilityModel] = useState<ProductAvailabilityState>();
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    const [productErpNumber, setProductErpNumber] = useState<string | undefined>(undefined);
    const [orderType, setOrderType] = useState<string>(orderTypeCookie || OrderTypes.STOCK);
    const [selectedProductModel, setSelectedProduct] = useState<ProductModel>();
    const [productInfo, setProductInfo] = useState<ProductInfo>();
    useEffect(() => {
        const lang = languages?.find(l => l.id === currentLanguage?.id);
        setSelectedLanguage(lang)

    }, [currentLanguage]);
    const handleOrderTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as OrderType;
        setOrderType(value);
        setOrderTypeCartModeCookie(value);
    };

    useEffect(() => {
        if (selectedProductModel && productAvailabilityState?.avilabilityLoadSuccess) {
            setProductAvilabilityModel(productAvailabilityState);
            setAvilabilityModel(true);
        }
        if (!productAvailabilityState?.avilabilityLoadSuccess && productAvailabilityState?.errorMessage) {
            setErrorMessage(productAvailabilityState?.errorMessage);
        }
        setIsLoading(productAvailabilityState?.isLoading);
    }, [productAvailabilityState]);

    useEffect(() => {
        reset();
    }, [location.pathname]);

    useEffect(() => {
        setSelectedProduct(undefined);
        searchProducts({ query: productErpNumber || "" });
    }, [productErpNumber]);

    useEffect(() => {
        if (!searchResults) {
            return;
        }
        if (searchResults.length === 1) {
            if (searchResults[0].id) {
                selectProduct(searchResults[0].id)
                return;
            }
        }
        if (searchResults.length === 0) {
            setErrorMessage(translate("Part Not Found."));
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: `${product.erpNumber} : ${product.title}`,
            optionValue: product.id || undefined,
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    useEffect(() => {
        if (selectedProductModel) {
            getProductAvailability({
                productNumber: selectedProductModel.detail?.modelNumber ?? selectedProductModel.productNumber,
                plantCode: session?.properties?.plantCode,
                quantity: qty ?? (productInfo?.qtyOrdered || 1),
                orderType,
                customerNumber: shiptoCustomer?.value?.customerSequence ?? billtoCustomer?.value?.customerSequence ?? "null",
            });

            let prodInfo = createFromProduct(selectedProductModel);
            prodInfo = {
                ...prodInfo,
                qtyOrdered: qty ?? 1,
            }

            setProductInfo(prodInfo);
        }
    }, [selectedProductModel, qty, orderType]);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setOptions([]);
        setSearchTerm(event.target.value);

        if (selectedProductModel) {
            clearProduct();
        }
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            setProductErpNumber(searchTerm);
        }
    };

    const clearButtonHandler = () => {
        setSearchTerm("");
        setProductErpNumber("");
        searchProducts({ query: "" });
        setSelectedProduct(undefined);
        setOptions([]);
    };

    const selectProduct = async (productId: string) => {
        setIsLoading(true);
        const result = await getProduct(productId);
        if ("error" in result) {
            setIsLoading(false);
            setErrorMessage(result.errorMessage);
        } else {
            setSelectedProduct(result);
            setIsLoading(false);
        }
    };

    const selecteProductModalTitle = (
        <StyledWrapper {...styles.selecteProductModalTitle}>
            <Typography {...styles.selectedProductErpNo}>{selectedProductModel?.detail?.modelNumber ?? selectedProductModel?.productNumber}</Typography>
            <Typography {...styles.selecteProductTitle}>
                {selectedProductModel?.productTitle}
            </Typography>
        </StyledWrapper>
    );
    const productContext: ProductContextModel = {
        product: selectedProductModel || {} as any,
        productInfo: productInfo || {} as any,
        onQtyOrderedChanged: setQty,
    };
    const setOrderTypeHandler = (value: string) => {
        setOrderType(value);
        setCookie("modalOrderType", value);
    }

    return (
        <>
            <LoadingOverlay loading={isSearching || isLoading}>
                <GridContainer {...styles.container} data-test-selector="Dana_Avilability_productSelector">
                    <GridItem width={12}>
                        <GridContainer isFullWidth={true} {...styles.radioGroupContainer}>
                            <GridItem width={12}>
                                {selectedLanguage && selectedLanguage.properties.region !== LARegion && (
                                    <RadioGroup
                                        id="orderAvailability"
                                        name="orderAvailability"
                                        value={orderType}
                                        onChangeHandler={handleOrderTypeChange}
                                        {...styles.orderTypeSelection}
                                    >
                                        <Radio
                                            {...styles.orderTypeStyle}
                                            data-test-selector="productSelector_orderType_STK"
                                            value={OrderTypes.STOCK}
                                        >
                                            {translate("Stock Order")}
                                        </Radio>
                                        <Radio
                                            {...styles.orderTypeStyle}
                                            data-test-selector="productSelector_orderType_PNA"
                                            value={OrderTypes.EMERGENCY}
                                        >
                                            {translate("Emergency Order")}
                                        </Radio>
                                    </RadioGroup>
                                )}
                                {selectedLanguage && selectedLanguage.properties.region === LARegion && (
                                    <Typography {...styles.orderType}>{translate("Stock Order")}</Typography>
                                )}

                            </GridItem>

                        </GridContainer>
                    </GridItem>

                    <GridItem {...styles.partNoSearhGrid} width={12}>
                        <TextField
                            type="text"
                            label={translate("Part No.")}
                            value={searchTerm}
                            iconProps={{
                                src: searchTerm ? X : "",
                            }}
                            iconClickableProps={{
                                onClick: clearButtonHandler,
                            }}
                            {...styles.partNoSearch}
                            onChange={onInputChanged}
                            data-test-selector="productSelector_partnumber"
                            onKeyPress={onKeyPress}
                        />
                        <Button
                            {...styles.selectButton}
                            onClick={() => setProductErpNumber(searchTerm)}
                            disabled={!searchTerm}
                            data-test-selector="productSelector_selectProduct"
                        >
                            {translate("Go")}
                        </Button>
                    </GridItem>
                    {searchTerm && options.length > 1 && (
                        <GridItem {...styles.searchGridItem} width={12}>
                            <Select
                                placeholder={translate("Suggestions")}
                                data-test-selector="locationSelector"
                                onChange={event => {
                                    selectProduct(event.currentTarget.value);
                                }}
                                value={selectedProductModel ? selectedProductModel?.id ?? "" : ""}
                                aria-label={translate("Suggestions")}
                            >
                                <option value="">{translate("Suggestions")}</option>
                                {options.map(c => (
                                    <option value={c.optionValue} key={c.optionValue}>
                                        {c.optionText}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    )}

                    {!isSearching && searchTerm && errorMessage && (
                        <StyledWrapper {...styles.errorMsgBox}>
                            <IconMemo src={WarningIcon} size={16} />
                            <Typography>{errorMessage}</Typography>
                        </StyledWrapper>
                    )}
                </GridContainer>
            </LoadingOverlay>
            <Modal
                headline={selecteProductModalTitle}
                isOpen={avilabilityModel}
                handleClose={() => setAvilabilityModel(!avilabilityModel)}
                {...styles.selectedProductModal}
            >
                <ProductContext.Provider value={productContext}>
                    {isLoading ? (<LoadingSpinner></LoadingSpinner>) : (
                        <ProductAvailabilityDetails
                            customErrorMessage={errorMessage ? errorMessage.toString() : ""}
                            productAvilabilityModel={productAvilabilityModel}
                            qty={qty.toString()}
                            quantityDisabled={false}
                            orderType={orderType}
                            setQty={setQty}
                            setOrderType={setOrderTypeHandler}
                            handleClose={() => setAvilabilityModel(false)}
                        />
                    )}
                </ProductContext.Provider>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckAvailabilityProductSelector);
